import type { MetricDefinition } from './metric-definitions';

export type MetricUnit = MetricDefinition['unit'];

export interface AxisDomain {
  min: number;
  max: number;
}

export interface ChartAxes {
  /** Axa din stânga: sumele în lei, dacă graficul are vreuna; altfel numărul (facturi, clienți noi). */
  left: { unit: MetricUnit; domain: AxisDomain };
  /**
   * Axa din dreapta, doar când graficul amestecă lei cu numere. Pe o singură axă, 400 de facturi lângă
   * zeci de mii de lei ieșeau o linie plată la zero (15.10.2026, „Facturi emise” pe dashboard).
   */
  right: { unit: 'count'; domain: AxisDomain } | null;
}

/** Intervalul axei: valorile + 15% spațiu, fără să coboare sub zero; o serie constantă primește ±1. */
export function axisDomain(values: number[]): AxisDomain {
  let mn = Infinity;
  let mx = -Infinity;
  for (const v of values) {
    if (v < mn) mn = v;
    if (v > mx) mx = v;
  }
  if (mn === Infinity) return { min: 0, max: 1 };
  const pad = (mx - mn) * 0.15 || 1;
  return { min: Math.max(0, mn - pad), max: mx + pad };
}

/** Numărul de intervale dintre liniile grilei (5 linii). */
export const Y_TICKS = 4;

/**
 * Pentru numere (facturi, clienți noi): pași întregi între liniile grilei, ca etichetele să nu se repete
 * (0–3 facturi pe zi dădea „3, 3, 2, 1, 0” cu pași de 0,86).
 */
export function integerDomain(d: AxisDomain, ticks = Y_TICKS): AxisDomain {
  const min = Math.floor(d.min);
  const step = Math.max(1, Math.ceil((d.max - min) / ticks));
  return { min, max: min + step * ticks };
}

export function buildChartAxes(series: Array<{ unit: MetricUnit; values: number[] }>): ChartAxes {
  const valuesOf = (unit: MetricUnit) => series.filter((s) => s.unit === unit).flatMap((s) => s.values);
  const hasMoney = series.some((s) => s.unit === 'money');
  const hasCount = series.some((s) => s.unit === 'count');
  if (hasMoney && hasCount) {
    return {
      left: { unit: 'money', domain: axisDomain(valuesOf('money')) },
      right: { unit: 'count', domain: integerDomain(axisDomain(valuesOf('count'))) },
    };
  }
  if (hasCount) return { left: { unit: 'count', domain: integerDomain(axisDomain(valuesOf('count'))) }, right: null };
  return { left: { unit: 'money', domain: axisDomain(valuesOf('money')) }, right: null };
}

/** Intervalul pe care se desenează o serie: axa din dreapta pentru numere, când există; altfel cea din stânga. */
export function domainForUnit(axes: ChartAxes, unit: MetricUnit): AxisDomain {
  return axes.right && unit === axes.right.unit ? axes.right.domain : axes.left.domain;
}
